import { Component, computed, inject, input, OnInit } from '@angular/core';
import { ILpCanvasComponent, ILpCanvasElementContext } from '@gametailors/leerplus';
import { LpRidLocationStop, LpRidLocationStopsCanvasComponent } from '../../../../typings/schema';
import { RidLocationStop, RidLocationStopState } from '../../components/rid-location-stop/rid-location-stop';
import { LEERPLUS } from '../../rid-game.utils';
import { RidHomeworkService } from '../../services/rid-homework.service';
import { RidJourneyService } from '../../services/rid-journey.service';

@Component({
  selector: 'rid-location-stops-canvas-component',
  imports: [RidLocationStop],
  templateUrl: './rid-location-stops-canvas-component.html',
  host: {
    class: 'block size-full [container-type:size]',
  },
})
export class RidLocationStopsCanvasComponent implements OnInit, ILpCanvasComponent<LpRidLocationStopsCanvasComponent> {
  protected readonly $lpApiService = inject(LEERPLUS.apiToken);
  protected readonly $ridHomeworkService = inject(RidHomeworkService);
  protected readonly $ridJourneyService = inject(RidJourneyService);

  comp = input.required<LpRidLocationStopsCanvasComponent>();
  context = input.required<ILpCanvasElementContext>();

  private readonly maxStopsPerRow = 5;
  private readonly cardAspectRatio = 7 / 8; // keep in sync with the aspect ratio of RidLocationStop
  protected readonly gap = '1.5cqw';

  rows = computed(() => {
    const stops = this.comp().stops;
    const rowCount = Math.max(1, Math.ceil(stops.length / this.maxStopsPerRow));
    const baseSize = Math.floor(stops.length / rowCount);
    const extraRows = stops.length % rowCount;

    const rows: LpRidLocationStop[][] = [];
    let offset = 0;
    for (let i = 0; i < rowCount; i++) {
      const size = baseSize + (i < extraRows ? 1 : 0);
      rows.push(stops.slice(offset, offset + size));
      offset += size;
    }

    return rows;
  });

  /** The widest the cards can be while all rows still fit the component, both horizontally and vertically. */
  cardWidth = computed(() => {
    const rows = this.rows();
    const columnCount = Math.max(1, ...rows.map((row) => row.length));
    const fitWidth = `(100cqw - ${columnCount - 1} * ${this.gap}) / ${columnCount}`;
    const fitHeight = `(100cqh - ${rows.length - 1} * ${this.gap}) / ${rows.length} * ${this.cardAspectRatio}`;
    return `min(${fitWidth}, ${fitHeight})`;
  });

  async ngOnInit(): Promise<void> {
    this.context().requirePointerEvents();
  }

  protected getStopState(stop: LpRidLocationStop): RidLocationStopState {
    if (this.$ridHomeworkService.isStopFinished(stop)) return 'FINISHED';

    if (this.$ridHomeworkService.isStopInProgress(stop)) return 'IN_PROGRESS';

    return this.$ridJourneyService.isStopClickable(stop) ? 'OPEN' : 'LOCKED';
  }
}
